import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { OrganizationService } from '@app/core/services/OrganizationService';
import { InvoiceService } from '@app/core/services/InvoiceService';
import { FileService } from '@app/core/services/FileService';
import { ReceivedInvoiceDialogComponent } from './received-invoice-dialog.component';

@Component({
  selector: 'app-received-invoices', standalone: true,
  imports: [CommonModule, FormsModule, MatFormFieldModule, MatInputModule, MatSelectModule, MatButtonModule, MatIconModule, MatDialogModule, MatPaginatorModule],
  template: `
    <div class="toolbar toolbar-filters" *ngIf="!initialInvoiceId">
      <mat-form-field class="received-search-field" appearance="fill" floatLabel="always"><mat-label>Sök mottagna fakturor</mat-label>
        <mat-icon matPrefix>search</mat-icon>
        <input matInput [(ngModel)]="filter" (keyup.enter)="load(true)">
      </mat-form-field>
      <mat-form-field appearance="fill" floatLabel="always"><mat-label>Status</mat-label>
        <mat-select [(ngModel)]="statusId" (ngModelChange)="load(true)">
          <mat-option [value]="0">Alla</mat-option>
          <mat-option *ngFor="let status of statuses" [value]="status.Id">{{status.Name}}</mat-option>
        </mat-select>
      </mat-form-field>
      <button mat-flat-button color="accent" type="button" (click)="load(true)">Sök</button>
    </div>
    <p *ngIf="error" role="alert">{{error}}</p>
    <div class="table-shell" *ngIf="!initialInvoiceId">
      <div class="scroll">
        <table><thead><tr><th>Faktura</th><th>Avsändare</th><th>Skapad</th><th>Typ</th><th>Belopp</th><th>Status</th><th>Underlag</th></tr></thead>
          <tbody *ngIf="loading"><tr *ngFor="let row of [1,2,3,4,5]"><td colspan="7"><div class="skeleton"></div></td></tr></tbody>
          <tbody *ngIf="!loading">
            <tr *ngFor="let invoice of invoices">
              <td>{{invoice.InvoiceNumber}}</td><td>{{invoice.Sender}}</td><td>{{date(invoice.Created)}}</td>
              <td>{{invoice.Type === 'CommissionCashInvoice' ? 'Provision' : 'Ersättning/delning'}}</td>
              <td>{{invoice.TotalInvoiceAmount | number:'1.0-2'}} {{invoice.Currency}}</td><td>{{invoice.Status}}</td>
              <td><button mat-stroked-button type="button" (click)="open(invoice.Id)">Visa underlag</button></td>
            </tr>
            <tr *ngIf="!invoices.length"><td colspan="7">Inga mottagna fakturor.</td></tr>
          </tbody>
        </table>
      </div>
      <mat-paginator [length]="total" [pageIndex]="pageIndex" [pageSize]="pageSize" [pageSizeOptions]="[10,25,50]" (page)="page($event)"></mat-paginator>
    </div>
    <section *ngIf="initialInvoiceId && detail" aria-label="Fakturaunderlag" class="detail">
      <h2>Underlag {{detail.Invoice.InvoiceNumber}}</h2>
      <p>{{detail.Invoice.Sender}} · {{detail.Invoice.Customer}} · {{detail.Invoice.Status}}</p>
      <p>Förfallodatum: {{date(detail.Invoice.PaymentDue)}} · Inbetald: {{date(detail.Invoice.PaymentDate)}}</p>
      <div *ngFor="let group of detail.Invoice.Groups">
        <p>{{group.Description}}: {{group.Amount | number:'1.0-2'}} {{detail.Invoice.Currency}}</p>
        <p *ngFor="let row of group.Rows">{{date(row.StartDate)}} – {{date(row.EndDate)}} {{row.Description}} {{row.HoursWorked}} tim {{row.MinutesWorked}} min</p>
      </div>
      <p *ngFor="let allowance of detail.Invoice.Allowances">{{allowance.ReasonForTravel}}: {{allowance.Sum | number:'1.0-2'}}</p>
      <p *ngFor="let deduction of detail.Invoice.Deductions">{{deduction.Description}}: {{deduction.Amount | number:'1.0-2'}}</p>
      <p>Fakturabelopp: {{detail.Invoice.TotalInvoiceAmount | number:'1.0-2'}} {{detail.Invoice.Currency}}</p>
      <p>Serviceavgift: {{detail.Invoice.ServiceFee | number:'1.0-2'}} · Moms på serviceavgift: {{detail.Invoice.ServiceFeeVat | number:'1.0-2'}}</p>
      <button mat-stroked-button *ngFor="let file of detail.Documents" (click)="files.openFile(file)">{{file.Name}}</button>
    </section>`,
  styleUrls: ['./received-invoices.component.scss']
})
export class ReceivedInvoicesComponent implements OnInit {
  @Input() organizationId = 0;
  @Input() initialInvoiceId = 0;
  invoices: any[] = []; statuses: any[] = []; detail: any = null;
  loading = true; error = ''; filter = ''; statusId = 0; total = 0; pageIndex = 0; pageSize = 10;
  constructor(private organizations: OrganizationService, private invoiceService: InvoiceService, public files: FileService,
    private cdr: ChangeDetectorRef, private dialog: MatDialog) {}
  ngOnInit(): void {
    if (this.initialInvoiceId) { this.open(this.initialInvoiceId); return; }
    this.invoiceService.getInvoiceStatuses().subscribe(statuses => { this.statuses = statuses; this.cdr.markForCheck(); });
    this.load();
  }
  date(value: string): string { return value ? value.slice(0, 10) : ''; }
  load(reset = false): void {
    if (reset) this.pageIndex = 0;
    this.loading = true; this.error = ''; this.detail = null;
    this.organizations.getReceivedInvoices(this.organizationId, this.pageIndex, this.pageSize, this.filter, this.statusId).subscribe({
      next: response => { this.invoices = response.Invoices; this.total = response.TotalInvoices; this.loading = false; this.cdr.markForCheck(); },
      error: () => { this.error = 'Mottagna fakturor kunde inte hämtas.'; this.loading = false; this.cdr.markForCheck(); }
    });
  }
  page(event: PageEvent): void { this.pageIndex = event.pageIndex; this.pageSize = event.pageSize; this.load(); }
  open(id: number): void {
    if (!this.initialInvoiceId) {
      const invoice = this.invoices.find(item => item.Id === id);
      this.dialog.open(ReceivedInvoiceDialogComponent, {
        width: '800px', maxWidth: 'calc(100vw - 32px)', maxHeight: 'calc(100dvh - 32px)',
        ariaLabelledBy: 'received-invoice-dialog-title',
        data: { organizationId: this.organizationId, invoiceId: id,
          invoiceNumber: invoice?.InvoiceNumber, type: invoice?.Type }
      });
      return;
    }
    this.error = '';
    this.organizations.getReceivedInvoice(this.organizationId, id).subscribe({
      next: detail => { this.detail = detail; this.cdr.markForCheck(); },
      error: () => { this.error = 'Underlaget kunde inte hämtas.'; this.cdr.markForCheck(); }
    });
  }
}
